// src/components/demos/DemoList.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDeleteDemos, useUpdateDemo } from "../../hooks/useDemoApi";
import { SquarePen, Trash2 } from "lucide-react";
import { EditDemoModal } from "./EditDemoModel";
import Swal from "sweetalert2";
import Loader from "../ui/Loader";
import { useAuth } from "../../hooks/AuthContext";
import Pagination from "../Models/Pagination";

export const DemoList = ({
  demos = [],
  isLoading,
  // isHrView = false,
  // editable = true,
  paginationData = {},
  onPageChange = () => { },
  statusFilter = [], // ✅ now accepts array like TaskList
  onSendInviteClick,
}) => {
  const [selectedDemoIds, setSelectedDemoIds] = useState(new Set());
  const [demoType, setDemoType] = useState();
  const { mutate: updateDemo, isLoading: isBulkUpdating } = useUpdateDemo();
  const { mutate: deleteDemoMutation } = useDeleteDemos();
  const [editingDemo, setEditingDemo] = useState(null);

  const selectAllCheckboxRef = useRef();
  const { user } = useAuth();

  // ✅ Filter the demos based on multiple statuses
  const filteredDemos = useMemo(() => {
    if (!statusFilter || statusFilter.length === 0) return demos || [];
    return demos?.filter((d) => statusFilter.includes(d.status)) || [];
  }, [demos, statusFilter]);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const allVisibleIds = filteredDemos.map((d) => d._id);
      const allSelected =
        allVisibleIds.length > 0 &&
        allVisibleIds.every((id) => selectedDemoIds.has(id));
      selectAllCheckboxRef.current.checked = allSelected;
      selectAllCheckboxRef.current.indeterminate =
        selectedDemoIds.size > 0 && !allSelected;
    }
  }, [selectedDemoIds, filteredDemos]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDemoIds(new Set(filteredDemos.map((d) => d._id)));
      setDemoType(new Set(filteredDemos.map((d) => d.type)));
    } else {
      setSelectedDemoIds(new Set());
      setDemoType(new Set());
    }
  };

  const handleSelectRow = (demoId, demoType) => {
    const newSelection = new Set(selectedDemoIds);
    if (newSelection.has(demoId)) {
      newSelection.delete(demoId);
    } else {
      newSelection.add(demoId);
    }
    setSelectedDemoIds(newSelection);
    setDemoType(demoType);
  };

  const handleBulkComplete = () => {
    if (selectedDemoIds.size === 0) return;
    updateDemo(
      { demoIds: Array.from(selectedDemoIds), status: "completed", updatedBy: user?.employeeProfile?._id },
      {
        onSuccess: (data) => {
          alert(data.message);
          setSelectedDemoIds(new Set());
        },
        onError: (error) => {
          alert(error.response?.data?.message || "An error occurred.");
        },
      }
    );
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete this Demo?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    }).then((res) => {
      if (res.isConfirmed) {
        deleteDemoMutation(id, {
          onSuccess: () => Swal.fire({
            title: "Deleted!",
            icon: "success",
            timer: 1000,
            showConfirmButton: false
          }),
        });
      }
    });
  };


  if (isLoading) {
    return <Loader />
  }

  if (filteredDemos.length === 0) {
    return (
      <p className="text-gray-500 mt-4">No data found .</p>
    );
  }


  return (
    <div className="space-y-4">
      {statusFilter.includes("pending") && (
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBulkComplete}
            disabled={selectedDemoIds.size === 0 || isBulkUpdating}
            className="px-4 py-2 text-sm font-medium text-white bg-[#2C4E86] rounded hover:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed"
          >
            {isBulkUpdating
              ? "Updating..."
              : `Mark Selected (${selectedDemoIds.size}) as Completed`}
          </button>
          <button
            onClick={() => onSendInviteClick(Array.from(selectedDemoIds), demoType)}
            disabled={selectedDemoIds.size === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-400"
          >
            Send Meet Link
          </button>
        </div>
      )}

      {/* ✅ Same table, now works for multiple statuses */}
      <div className="overflow-x-auto bg-white rounded-lg shadow border">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {statusFilter.includes("pending") && (
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    ref={selectAllCheckboxRef}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Function Name
              </th>

              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Created By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Host By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDemos.map((demo) => (
              <tr
                key={demo._id}
                className={
                  selectedDemoIds.has(demo._id)
                    ? "bg-blue-50"
                    : "hover:bg-gray-50"
                }
              >
                {statusFilter.includes("pending") && (
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedDemoIds.has(demo._id, demo.type)}
                      onChange={() => handleSelectRow(demo._id, demo.type)}
                    />
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap  text-sm font-medium text-gray-900">
                  {demo?.demoName}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {demo?.createdBy?.name}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {demo?.hostBy?.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  <p
                    className={`px-3 rounded-full text-center font-medium w-fit 
      ${demo?.type === "demo" ? "bg-blue-100 text-blue-700" : ""} 
      ${demo?.type === "training" ? "bg-green-100 text-green-700" : ""}`}
                  >
                    {demo?.type || "-"}
                  </p>
                </td>



                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${demo.status === "completed"
                      ? "bg-green-200 text-green-800"
                      : "bg-yellow-200 text-yellow-800"
                      }`}
                  >
                    {demo.status}
                  </span>
                </td>
                {/* <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {demo.invitationSent ? (
                    <span className="flex items-center text-green-600">
                      Sent
                    </span>
                  ) : (
                    <span className="flex items-center text-gray-500">
                      Not Sent
                    </span>
                  )}
                </td> */}
                <td className="px-6 py-4 text-sm">
                  <div className="flex gap-4">
                    <button
                      onClick={() => setEditingDemo(demo)}
                      className="text-green-500 hover:text-green-600"
                    >
                      <SquarePen />
                    </button>
                    {user?.role !== "employee" && (
                      <button
                        onClick={() => handleDelete(demo._id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 />
                      </button>
                    )}
                  </div>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EditDemoModal demo={editingDemo} onClose={() => setEditingDemo(null)} />

      {/* Pagination */}
      <Pagination
        currentPage={paginationData?.page || 1}
        totalPages={paginationData?.pages || 1}
        onPageChange={onPageChange}
        maxVisible={3}
      />

    </div>
  );
};
